import { BookOpen, CircleHelp } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useInRouterContext, useLocation } from "react-router";

import { guideForPath } from "@/help/topics";
import { cn } from "@/lib/utils";

export interface PageHeaderProps {
  title: string;
  /** One short sentence under the title (what this page is for). */
  description?: string;
  /**
   * The ONE main thing to do on this page (design/04 §0) — a single element (Button / Link).
   * Everything else goes to `secondaryActions`.
   */
  primaryAction?: ReactNode;
  /** Less important actions, shown smaller next to the main one. */
  secondaryActions?: ReactNode;
  /** Short help shown behind the "?" button. */
  help?: ReactNode;
  /** e.g. a back link above the title. */
  eyebrow?: ReactNode;
  className?: string;
}

/** "Guide for this page" (T11-47): the 30-second guide of the job done on this screen, if any. */
function GuideLink() {
  const { t } = useTranslation("help");
  const { pathname } = useLocation();
  const guide = guideForPath(pathname);
  if (!guide) {
    return null;
  }
  return (
    <Link
      to={`/help/${guide}`}
      data-slot="page-guide"
      aria-label={t("page.open_guide")}
      title={t("page.open_guide")}
      className="inline-flex size-touch items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
    >
      <BookOpen className="size-5" aria-hidden />
    </Link>
  );
}

/**
 * Standard page top: title (the page's only `<h1>`), one primary action, optional help and the
 * guide of the page (T11-47). Pages must not render their own `<h1>` next to it.
 */
export function PageHeader({
  title,
  description,
  primaryAction,
  secondaryActions,
  help,
  eyebrow,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const inRouter = useInRouterContext();
  return (
    <header data-slot="page-header" className={cn("flex flex-col gap-3", className)}>
      {eyebrow ? <div className="text-sm">{eyebrow}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl leading-tight font-bold break-words">{title}</h1>
            {help ? (
              <button
                type="button"
                className="inline-flex size-touch items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
                aria-expanded={helpOpen}
                aria-controls={helpId}
                aria-label={t("page.help")}
                onClick={() => {
                  setHelpOpen((open) => !open);
                }}
              >
                <CircleHelp className="size-5" aria-hidden />
              </button>
            ) : null}
            {inRouter ? <GuideLink /> : null}
          </div>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {primaryAction || secondaryActions ? (
          <div className="flex flex-wrap items-center gap-2" data-slot="page-actions">
            {secondaryActions}
            {primaryAction ? <div data-slot="primary-action">{primaryAction}</div> : null}
          </div>
        ) : null}
      </div>
      {help && helpOpen ? (
        <div id={helpId} role="note" className="rounded-md border bg-muted p-3 text-sm">
          {help}
        </div>
      ) : null}
    </header>
  );
}
